import React, { FC } from 'react';
import styled, { css } from 'styled-components';
import * as DataTableTypes from '../utils/DataTableTypes';
import { StyledTd } from '../utils/DataTableStyledElements';
import { getRowHeightWithPx } from '../utils/getDataTableHeight';

export interface TrProps
  extends Omit<React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>, 'css' | 'ref'> {
  dataTest: string | undefined;
  rowHeight: DataTableTypes.DataTableRowHeight | undefined;
  isSelected: boolean | undefined;
  isSkeleton: boolean | undefined;
  resizeTableColumns: boolean;
}

export const Tr: FC<React.PropsWithChildren<TrProps>> = ({
  isSkeleton,
  rowHeight,
  children,
  dataTest = 'data-table-row',
  isSelected,
  resizeTableColumns,
  ...props
}) => (
  <StyledTr
    $resizeTableColumns={resizeTableColumns}
    data-test={dataTest}
    {...props}
    $isSkeleton={isSkeleton}
    $rowHeight={rowHeight}
    $isSelected={isSelected}
  >
    {children}
  </StyledTr>
);

interface StyledTrProps {
  $isSkeleton: boolean | undefined;
  $rowHeight: DataTableTypes.DataTableRowHeight | undefined;
  $isSelected: boolean | undefined;
  $resizeTableColumns: boolean;
}

const stickyCSS = css<StyledTrProps>`
  [data-sticky-first-right-td] {
    right: 0;
  }
  [data-sticky-first-left-td] {
    left: 0;
  }
  [data-sticky-first-right-td],
  [data-sticky-first-left-td] {
    box-sizing: border-box;
    position: sticky;
    z-index: 3;
    justify-content: flex-end;
    ::before {
      content: '';
      height: 100%;
      top: 0;
      width: 30px !important;
      position: absolute;
    }
    color: ${({ theme, $isSelected }) => ($isSelected ? theme.colorOnSecondaryContainer : theme.colorOnSurface)};
    background: ${({ theme, $isSelected }) => ($isSelected ? theme.colorSecondaryContainer : theme.colorSurface)};
  }

  [data-sticky-first-right-td]::before {
    left: -30px !important;
    background: ${({ theme, $isSelected }) =>
      $isSelected
        ? ` 
          linear-gradient(
          90deg,
          ${theme.colorSecondaryContainerFade0} 0%,
          ${theme.colorSecondaryContainerFade1} 60%,
          ${theme.colorSecondaryContainerFade1} 100%
        )`
        : `
          linear-gradient(
          90deg,
          ${theme.colorSurfaceFade0} 0%,
          ${theme.colorSurfaceFade1} 60%,
          ${theme.colorSurfaceFade1} 100%
       )`};
  }

  [data-sticky-first-left-td]::before {
    right: -30px !important;
    background: ${({ theme, $isSelected }) =>
      $isSelected
        ? ` 
          linear-gradient(
          90deg,
          ${theme.colorSecondaryContainerFade1} 100%
          ${theme.colorSecondaryContainerFade1} 60%,
          ${theme.colorSecondaryContainerFade0} 0%,
        )`
        : `
          linear-gradient(
          90deg,
          ${theme.colorSurfaceFade1} 100%,
          ${theme.colorSurfaceFade1} 60%,
          ${theme.colorSurfaceFade0} 0%
        )`};
  }

  &:hover {
    [data-sticky-first-left-td]::before {
      background: ${({ theme, $isSelected }) =>
        $isSelected
          ? ` 
          linear-gradient(
          90deg,
          ${theme.colorSecondaryContainer} 100%
          ${theme.colorSecondaryContainer} 60%,
          ${theme.colorSecondaryContainerFade0} 0%,
        )`
          : ` 
          linear-gradient(
          90deg,
          ${theme.colorSurface} 100%
          ${theme.colorSurface} 60%,
          ${theme.colorSurfaceFade0} 0%,
        )`};
    }
  }

  &:hover {
    [data-sticky-first-right-td]::before {
      left: 0 !important;
      background: ${({ theme, $isSelected }) =>
        $isSelected
          ? ` 
          linear-gradient(
          90deg,
          ${theme.colorSecondaryContainerFade0} 0%,
          ${theme.colorSecondaryContainer} 60%,
          ${theme.colorSecondaryContainer} 100%
        )`
          : ` 
          linear-gradient(
          90deg,
          ${theme.stateOnSurfaceHoverStateLayerColor} 0%,
          ${theme.colorSurface} 60%,
          ${theme.colorSurface} 100%
        )`};
    }
  }
`;

const colorStatesCss = css<StyledTrProps>`
  background: ${({ theme, $isSelected }) => ($isSelected ? theme.colorSecondaryContainer : 'inherit')};
  &:not([data-test='data-table-header']) {
    ${stickyCSS}
    > div {
      background: ${({ theme, $isSelected }) => ($isSelected ? theme.colorSecondaryContainer : 'inherit')};
    }
    &:hover {
      > div {
        background: ${({ theme, $isSelected }) => ($isSelected ? theme.colorSecondaryContainer : theme.colorSurface)};
        position: relative;

        &::before {
          content: '';
          top: 0;
          left: 0;
          height: 100%;
          width: 100%;
          position: absolute;
          pointer-events: none;
          background: ${({ theme, $isSelected }) =>
            $isSelected
              ? theme.stateOnSecondaryContainerHoverStateLayerColor
              : theme.stateOnSurfaceHoverStateLayerColor};
        }
      }
    }
  }
`;

const StyledTr = styled.div<StyledTrProps>`
  display: flex;
  height: ${({ $rowHeight, $isSkeleton }) => getRowHeightWithPx($rowHeight, $isSkeleton)};
  :last-child {
    ${StyledTd} {
      border-bottom: 0;
    }
  }
  ${({ $isSkeleton }) => ($isSkeleton ? '' : colorStatesCss)}
`;
